import { CONSENT_SOURCE_LABELS } from '@fundline/shared/campaigns/eligibility';
import {
  type CampaignSettingsForm,
  checkCampaignSettingsForm,
} from '@fundline/shared/campaigns/settings';
import { type FormEvent, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { TextField } from '../../components/ui/Field.tsx';
import { Card, PageHeader, Spinner } from '../../components/ui/Layout.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import { SettingsTabs } from '../settings/SettingsTabs.tsx';
import {
  type CampaignSettingsData,
  useCampaignSettingsQuery,
  useSaveCampaignSettings,
} from './use-campaigns.ts';

/**
 * Settings → Campaigns (M9): the per-campaign spending limit and the workspace's daily segment limit,
 * and which consent counts for campaigns (read-only: it changes only after the lawyer's review).
 */
export function CampaignSettingsPage() {
  const { workspaceId } = useWorkspaceClaims();
  const settings = useCampaignSettingsQuery(workspaceId);
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Workspace settings"
        description="Limits for campaigns, and which consent lets a merchant get one."
      />
      <SettingsTabs />
      {settings.isPending ? <Spinner /> : null}
      {settings.isError ? <Alert tone="error">{settings.error.message}</Alert> : null}
      {settings.data ? <CampaignSettingsFormCard data={settings.data} /> : null}
      {settings.data ? <ConsentCard sources={settings.data.consentSources} /> : null}
    </div>
  );
}

function CampaignSettingsFormCard({ data }: { data: CampaignSettingsData }) {
  const { workspaceId } = useWorkspaceClaims();
  const save = useSaveCampaignSettings(workspaceId);
  const [form, setForm] = useState<CampaignSettingsForm>(data.form);
  const [submitted, setSubmitted] = useState(false);
  const issues = checkCampaignSettingsForm(form);
  const shown = submitted ? issues : {};
  const set = <K extends keyof CampaignSettingsForm>(key: K, value: string) => {
    save.reset();
    setForm((f) => ({ ...f, [key]: value }));
  };

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (Object.keys(issues).length > 0) return;
    save.mutate({ form, rawSettings: data.rawSettings });
  }

  return (
    <Card title="Limits">
      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <TextField
          label="Most one campaign may cost (estimate, $)"
          hint="A campaign whose estimate is above this can't launch."
          inputMode="decimal"
          value={form.spendLimit}
          error={shown.spendLimit}
          onChange={(e) => set('spendLimit', e.target.value)}
        />
        <TextField
          label="Segments your workspace may send a day"
          hint="Your carriers' daily limit (2,000 for a new 10DLC brand). Campaigns stop at 90% so your team can still reply; the day resets at midnight Pacific."
          inputMode="numeric"
          value={form.dailySegmentLimit}
          error={shown.dailySegmentLimit}
          onChange={(e) => set('dailySegmentLimit', e.target.value)}
        />
        {save.isError ? <Alert tone="error">{save.error.message}</Alert> : null}
        {save.isSuccess ? <Alert tone="success">Saved.</Alert> : null}
        <div>
          <Button type="submit" busy={save.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Card>
  );
}

function ConsentCard({ sources }: { sources: string[] }) {
  return (
    <Card title="Consent that counts for campaigns">
      <ul
        className="callout flex list-disc flex-col gap-1 pl-5 text-ink"
        data-testid="campaign-consent"
      >
        {sources.map((s) => (
          <li key={s}>{CONSENT_SOURCE_LABELS[s] ?? s}</li>
        ))}
      </ul>
      <p className="footnote mt-2 text-ink-muted">
        Merchants whose only consent is something else (verbal, other, or a text they sent first)
        are skipped. This list changes only after a lawyer's review; ask Fundline support.
      </p>
    </Card>
  );
}
